import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  SafeAreaView,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { NavigationProp } from '@react-navigation/native';
import OrnamentBackground from '../../components/OrnamentBackground';
import { API_URL } from '../../config';
import { ms, s, vs } from '../../utils/responsive';

import { createLogger } from '../../utils/logger';
const logger = createLogger('ForgotPassword');

const ForgotPasswordScreen = ({
  navigation,
}: {
  navigation: NavigationProp<any>;
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleContinue = async () => {
    // Normalize the email up-front so every downstream call uses the same
    // canonical form the login screen and backend expect.
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      Alert.alert('Error', 'Please enter your email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      Alert.alert('Error', 'Please enter a valid email address.');
      return;
    }

    setIsLoading(true);

    try {
      // Call backend to initiate password reset
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      const result = await response.json();

      if (response.ok) {
        // Navigate to password verification screen
        navigation.navigate('PasswordVerificationCodeScreen', {
          identifier: normalizedEmail,
          type: 'email'
        });
      } else {
        // Show error but still navigate for security (don't reveal if email exists)
        // This prevents email enumeration attacks
        Alert.alert(
          'Check Your Email',
          'If an account exists with this email, you will receive a password reset code.',
          [
            {
              text: 'OK',
              onPress: () => navigation.navigate('PasswordVerificationCodeScreen', {
                identifier: normalizedEmail,
                type: 'email'
              })
            }
          ]
        );
      }
    } catch (error) {
      logger.error('Forgot password error:', error);
      // Still show success message for security
      Alert.alert(
        'Check Your Email',
        'If an account exists with this email, you will receive a password reset code.',
        [
          {
            text: 'OK',
            onPress: () => navigation.navigate('PasswordVerificationCodeScreen', {
              identifier: normalizedEmail,
              type: 'email'
            })
          }
        ]
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <View style={styles.topSection}>
        <Image source={require('../../assets/logo.png')} style={styles.logo} />
        <Text style={styles.welcomeText}>Forgot Password?</Text>
        <Text style={styles.description}>
          Don't worry! It happens. Please enter your email address and we'll send you a verification code to reset your password.
        </Text>
      </View>

      <View style={styles.mainContainer}>
        <View style={styles.inputContainer}>
          <ScrollView style={styles.scrollView}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Email Address</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter your email address"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                placeholderTextColor="#A9A9A9"
                editable={!isLoading}
              />
              <Text style={styles.inputHint}>Enter the email address associated with your account</Text>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.continueButton, isLoading && styles.continueButtonDisabled]}
                onPress={handleContinue}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.continueButtonText}>Continue</Text>
                )}
              </TouchableOpacity>
            </View>

            <TouchableOpacity 
              style={styles.backToLoginContainer}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.backToLoginText}>
                Remember your password? <Text style={styles.backToLoginLink}>Back to Login</Text>
              </Text>
            </TouchableOpacity>

            <View style={styles.bottomPadding} />
          </ScrollView>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
    flex: 0.78,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  welcomeText: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: ms(20),
    paddingHorizontal: ms(40),
  },
  mainContainer: {
    flex: 0.88,
    position: 'relative',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(30),
    position: 'relative',
    zIndex: 2,
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  inputGroup: {
    marginBottom: ms(24),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: 1,
    borderColor: '#d8dbdd',
  },
  inputHint: {
    fontSize: ms(12, 0.3),
    color: '#A9A9A9',
    marginTop: ms(4),
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  continueButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  continueButtonDisabled: {
    opacity: 0.6,
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  backToLoginContainer: {
    alignItems: 'center',
    marginTop: ms(10),
  },
  backToLoginText: {
    color: '#6B7280',
    fontSize: ms(14, 0.3),
  },
  backToLoginLink: {
    color: '#29BFC0',
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(30),
  },
});

export default ForgotPasswordScreen;
